import React, { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"
import { changeLanguage } from "../../redux/environmentRedux"
import { register } from "../../redux/apiCalls"
import { Helmet } from "react-helmet"
// Components
import Navbar from "../../components/Navbar/Navbar"
import Footer from "../../components/Footer/Footer"
import Checkbox from "../../components/Checkbox/Checkbox"
import Button from "../../components/Button/Button"
import AccountAddress from "../../components/AccountAddress/AccountAddress"
// Styles
import {
   Container,
   Form,
   FormItem,
   Message,
   StyledInput,
   StyledInputs,
   StyledLabel,
   StyledOption,
   StyledSelect,
   Title,
   Wrapper,
} from "./Register.styles"

const Register = () => {
   const dispatch = useDispatch()
   const navigate = useNavigate()
   const { t, i18n } = useTranslation()

   const [isComplete, setIsComplete] = useState(false)
   const [errorMessage, setErrorMessage] = useState(null)
   const [passwordCheck, setPasswordCheck] = useState("")
   const [userInputs, setUserInputs] = useState({
      username: "",
      email: "",
      password: "",
      type: "public",
   })
   const [deliveryAddress, setDeliveryAddress] = useState({
      name: "",
      street: "",
      zipCode: "",
      city: "",
      region: "",
      country: "",
      info: "",
   })
   const [invoiceAddress, setInvoiceAddress] = useState({
      name: "",
      street: "",
      zipCode: "",
      city: "",
      region: "",
      country: "",
      info: "",
   })

   useEffect(() => {
      // Vérification des deux mots de passe
      if (userInputs.password !== passwordCheck && passwordCheck !== "") {
         setErrorMessage(t("registerPage.message.m1"))
      }
      // Vérification du format de l'adresse email
      if (
         (!userInputs.email.includes("@") || !userInputs.email.includes(".")) &&
         userInputs.email !== ""
      ) {
         setErrorMessage(t("registerPage.message.m2"))
      }
      // Champs obligatoires ok pour validation du formulaire
      if (
         userInputs.email.includes("@") &&
         userInputs.email.includes(".") &&
         userInputs.password === passwordCheck &&
         passwordCheck !== ""
      ) {
         setIsComplete(true)
         setErrorMessage(null)
      }
      setUserInputs((prev) => {
         return {
            ...prev,
            deliveryAddress: deliveryAddress,
            invoiceAddress: invoiceAddress,
         }
      })
   }, [
      userInputs.username,
      userInputs.email,
      userInputs.password,
      passwordCheck,
      t,
      deliveryAddress,
      invoiceAddress,
   ])

   const [emailing, setEmailing] = useState(false)
   const emailingChange = () => {
      setUserInputs((prev) => {
         return { ...prev, emailing: !emailing }
      })
      setEmailing((current) => !current)
   }

   const handleChange = (e) => {
      setUserInputs((prev) => {
         return { ...prev, [e.target.name]: e.target.value }
      })
   }

   const handleLang = (e) => {
      setUserInputs((prev) => {
         return { ...prev, language: e.target.value }
      })
      dispatch(changeLanguage(e.target.value))
      i18n.changeLanguage(e.target.value)
   }

   const handleSubmit = async (e) => {
      e.preventDefault()

      let user = userInputs
      // Pas de nom d'utilisateur --> utilisation de l'email comme username
      if (user.username === "") {
         user.username = user.email
      }

      const res = await register(user, dispatch)
      if (res === "success") {
         setErrorMessage(t("registerPage.message.m3"))
         setTimeout(() => navigate("/"), 4000)
      } else {
         if (JSON.stringify(res).includes("email")) {
            setErrorMessage(t("registerPage.message.m4"))
         } else if (JSON.stringify(res).includes("username")) {
            setErrorMessage(t("registerPage.message.m5"))
         } else {
            setErrorMessage(JSON.stringify(res))
         }
      }
   }

   return (
      <>
         <Helmet>
            <title>{t("registerPage.meta.meta_title")}</title>
            <meta name="robots" content="noindex, nofollow" />
         </Helmet>
         <Container>
            <Navbar bgImageColor="light" />
            <Wrapper>
               <Form>
                  <Title>{t("registerPage.title.t1")}</Title>
                  <StyledInputs>
                     <FormItem>
                        <StyledLabel>{t("registerPage.label.l5")}</StyledLabel>
                        <StyledInput
                           type="text"
                           name="username"
                           onChange={handleChange}
                        />
                     </FormItem>
                     <FormItem>
                        <StyledLabel color="secondary">
                           {t("registerPage.label.l6")}*
                        </StyledLabel>
                        <StyledInput
                           type="text"
                           name="email"
                           onChange={handleChange}
                        />
                     </FormItem>
                     <FormItem>
                        <StyledLabel color="secondary">
                           {t("registerPage.label.l1")}
                        </StyledLabel>
                        <StyledInput
                           type="password"
                           name="password"
                           onChange={handleChange}
                        />
                     </FormItem>
                     <FormItem>
                        <StyledLabel color="secondary">
                           {t("registerPage.label.l2")}
                        </StyledLabel>
                        <StyledInput
                           type="password"
                           onChange={(e) => setPasswordCheck(e.target.value)}
                        />
                     </FormItem>
                     <FormItem>
                        <StyledLabel>{t("accountPage.label.l3")}</StyledLabel>
                        <StyledInput
                           type="text"
                           name="firstName"
                           onChange={handleChange}
                        />
                     </FormItem>
                     <FormItem>
                        <StyledLabel>{t("accountPage.label.l4")}</StyledLabel>
                        <StyledInput
                           type="text"
                           name="lastName"
                           onChange={handleChange}
                        />
                     </FormItem>
                     <FormItem>
                        <StyledLabel>{t("accountPage.label.l5")}</StyledLabel>
                        <StyledSelect
                           name="language"
                           id="language"
                           defaultValue="en"
                           onChange={handleLang}
                        >
                           <StyledOption value="en">English</StyledOption>
                           <StyledOption value="fr">Français</StyledOption>
                        </StyledSelect>
                     </FormItem>
                     <FormItem>
                        <StyledLabel>{t("accountPage.label.l6")}</StyledLabel>
                        <StyledInput
                           type="text"
                           name="phone"
                           onChange={handleChange}
                        />
                     </FormItem>
                     <FormItem>
                        <StyledLabel>{t("accountPage.label.l7")}</StyledLabel>
                        <StyledInput
                           type="text"
                           name="companyName"
                           onChange={handleChange}
                        />
                     </FormItem>
                     <FormItem>
                        <StyledLabel>
                           <Checkbox
                              checked={emailing}
                              onChange={emailingChange}
                           />
                           {t("accountPage.label.l9")}
                        </StyledLabel>
                     </FormItem>
                  </StyledInputs>
                  <AccountAddress
                     role="delivery"
                     setAddress={setDeliveryAddress}
                  />
                  <AccountAddress
                     role="invoice"
                     setAddress={setInvoiceAddress}
                  />
                  <StyledLabel color="secondary">
                     {t("registerPage.label.l4")}
                  </StyledLabel>
                  <Button
                     callback={(e) => handleSubmit(e)}
                     text={t("registerPage.button.b1")}
                     disabled={!isComplete}
                     submit
                  />
                  <Message>{errorMessage}</Message>
               </Form>
            </Wrapper>
            <Footer />
         </Container>
      </>
   )
}

export default Register
